/** @jsxRuntime classic */
/** @jsx jsx */
// @ts-check
import {
  Box,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  Container,
  Grid,
  Label,
  Link,
  Text,
  Title,
} from '@bottlebooks/gatsby-theme-base';
import ExhibitorsList from '@bottlebooks/gatsby-theme-event/src/components/ExhibitorsList/ExhibitorsList';
import ExhibitorsPageCards from '@bottlebooks/gatsby-theme-event/src/components/ExhibitorsPage/ExhibitorsPageCards';
import Layout from '@bottlebooks/gatsby-theme-event/src/components/Layout/Layout';
import { graphql } from 'gatsby';
import { jsx } from 'theme-ui';
// eslint-disable-next-line import/no-unresolved
import FloorPlansSection from '../../../components/FloorPlansSection';
import { getRoomColors } from '../../../getExhibitorRoomColors';
import useBottlebooksRegistrations from '../../../useBottlebooksData';
import { Trans } from '@lingui/macro';
// eslint-disable-next-line import/no-unresolved
import BannerAd from '../../../components/BannerAd';

export default function RoomPage({ data, pageContext, ...rest }) {
  const { room, previous, next } = afterQuery(data);
  const { shadow, primaries } = getRoomColors(room);
  const { addRegistration } = useBottlebooksRegistrations();

  return (
    <Layout locale={pageContext.locale}>
      <RoomHeader room={room} previous={previous} next={next} />

      <Box
        sx={{
          backgroundColor: primaries['20'],
          boxShadow: `1px 3px 10px inset ${shadow}`,
          border: 1,
          borderColor: shadow,
        }}
      >
        <Container>
          <FloorPlansSection floorId={room.floor?.slug} roomId={room.slug} />
        </Container>
      </Box>

      {room.stands?.length > 0 && (
        <ExhibitorsList
          sx={{
            gridAutoFlow: 'dense',
            paddingX: 'gutter',
            paddingY: 3,
            backgroundColor: primaries['10'],
          }}
        >
          <ExhibitorsPageCards
            exhibitors={room.stands?.map(({ exhibitor }) =>
              addRegistration(exhibitor)
            )}
            sx={{ outlineWidth: 1, boxShadow: 'none' }}
          />
        </ExhibitorsList>
      )}
      <NextRoom room={next} />
      <Box
        sx={{
          backgroundColor: primaries['10'],
          textAlign: 'center',
          paddingY: 4,
        }}
      >
        <BannerAd sx={{ marginX: 'auto' }} />
      </Box>
    </Layout>
  );
}

function RoomHeader({ room, previous, next }) {
  return (
    <Grid
      gap={0}
      sx={{
        gridTemplateRows: '5fr 2fr auto',
        backgroundColor: room.colors?.primary,
        color: room.colors?.onPrimary,
      }}
    >
      <Container
        sx={{
          paddingTop: 2,
          display: 'grid',
          width: '100%',
          alignItems: 'stretch',
        }}
      >
        <Nav room={room} next={next} previous={previous} />
        <Title
          variant="large"
          sx={{
            textTransform: 'uppercase',
            paddingTop: 4,
            paddingBottom: 1,
            marginTop: 'auto',
          }}
        >
          {room.name}
        </Title>
      </Container>
      <Box
        sx={{
          backgroundColor: room.colors?.onPrimary,
          paddingTop: 1,
          paddingBottom: 3,
        }}
      >
        <Container>
          <Text
            sx={{
              color: room.colors?.primary,
              textTransform: 'uppercase',
              minHeight: '3em',
            }}
          >
            {room.subtitle}
          </Text>
        </Container>
      </Box>
    </Grid>
  );
}

function Nav({ room, next, previous, ...rest }) {
  return (
    <Grid
      sx={{ gridTemplateColumns: ['32px 1fr 32px', '1fr 1fr 1fr'] }}
      {...rest}
    >
      <Text variant="small">
        {previous && (
          <NavLink
            color={room.colors?.onPrimary}
            to={`/exhibitors/${previous.floor.slug}/${previous.slug}`}
          >
            <ChevronLeftIcon />{' '}
            <span sx={{ display: ['none', 'inline'] }}>{previous.name}</span>
          </NavLink>
        )}
      </Text>
      <Text variant="small" sx={{ textAlign: 'center' }}>
        <NavLink
          to={`/exhibitors/${room.floor.slug}`}
          color={room.colors?.onPrimary}
        >
          <ChevronUpIcon /> {room.floor.name}
        </NavLink>
      </Text>
      <Text variant="small" sx={{ textAlign: 'right' }}>
        {next && (
          <NavLink
            to={`/exhibitors/${next.floor.slug}/${next.slug}`}
            color={room.colors?.onPrimary}
          >
            <span sx={{ display: ['none', 'inline'] }}>{next.name}</span>{' '}
            <ChevronRightIcon />
          </NavLink>
        )}
      </Text>
    </Grid>
  );
}

function NavLink({ color, children, to, ...rest }) {
  return (
    <Link
      to={to}
      sx={{
        color: color,
        opacity: 0.7,
        transition: 'opacity',
        ':hover': { opacity: 1, color: color },
      }}
      {...rest}
    >
      {children}
    </Link>
  );
}

function NextRoom({ room }) {
  if (!room) return null;
  return (
    <Box>
      <Link
        to={`/exhibitors/${room.floor.slug}/${room.slug}`}
        sx={{
          display: 'block',
          color: room.colors?.onPrimary,

          transition: 'opacity',
          ':hover': { opacity: 0.95, color: room.colors?.onPrimary },
        }}
      >
        <Box
          sx={{
            backgroundColor: room.colors?.primary,
            color: room.colors?.onPrimary,
          }}
        >
          <Container sx={{ paddingTop: 5, width: '100%' }}>
            <Label>
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
              /* @ts-ignore */}
              <Trans>Next room:</Trans>
            </Label>
            <Title
              variant="large"
              sx={{ textTransform: 'uppercase', paddingBottom: 1 }}
            >
              {room.name} <ChevronRightIcon />
            </Title>
          </Container>
        </Box>
        <Box
          sx={{
            backgroundColor: room.colors?.onPrimary,
            paddingTop: 1,
            paddingBottom: 3,
          }}
        >
          <Container>
            <Text
              sx={{
                color: room.colors?.primary,
                textTransform: 'uppercase',
                minHeight: '3em',
              }}
            >
              {room.subtitle}
            </Text>
          </Container>
        </Box>
      </Link>
    </Box>
  );
}

export const query = graphql`
  query ($slug: String) {
    room(slug: { eq: $slug }) {
      id
      name
      slug
      subtitle
      colors {
        onPrimary
        primary
      }
      floor {
        name
        slug
      }
      standCount
      stands {
        exhibitor {
          exhibitorId
          name
          ...ExhibitorCard
        }
      }
    }
    allRoom {
      edges {
        node {
          id
        }
        previous {
          slug
          name
          floor {
            slug
          }
        }
        next {
          slug
          name
          subtitle
          colors {
            primary
            onPrimary
          }
          floor {
            slug
          }
        }
      }
    }
  }
`;

function afterQuery({ room, allRoom }) {
  const { previous, next } =
    allRoom.edges?.find(({ node }) => node.id === room.id) || {};
  return { room, previous, next };
}
